/**
 * FAQ 관리 — 레거시 `faq.js` 포팅
 */
import type { FaqCategory, FaqItem } from '@/entities/board/api/board.api';
import {
  useCreateFaq,
  useDeleteFaq,
  useFaqs,
  useUpdateFaq,
} from '@/entities/board/hooks/useBoard';
import {
  FAQ_CATEGORIES,
  getFaqCategoryBadgeVariant,
  getFaqCategoryLabel,
} from '@/pages/board/boardUtils';
import { useConfirmDialog } from '@/shared/components/ConfirmDialogProvider';
import { DataCard } from '@/shared/components/DataCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { ToolbarRow } from '@/shared/components/ui/row';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { cn } from '@/shared/lib/utils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

export const FaqPage: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirmDialog();
  const { data: faqs = [], isLoading, refetch } = useFaqs();
  const createFaq = useCreateFaq();
  const updateFaq = useUpdateFaq();
  const deleteFaq = useDeleteFaq();

  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<FaqItem | null>(null);
  const [form, setForm] = useState({
    category: 'USER' as FaqCategory,
    question: '',
    answer: '',
  });

  const filtered =
    categoryFilter === 'ALL'
      ? faqs
      : faqs.filter((f) => f.category === categoryFilter);

  const openCreate = () => {
    setEditing(null);
    setForm({ category: 'USER', question: '', answer: '' });
    setOpen(true);
  };

  const openEdit = (faq: FaqItem) => {
    setEditing(faq);
    setForm({
      category: faq.category,
      question: faq.question,
      answer: faq.answer,
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error('질문과 답변을 입력해 주세요.');
      return;
    }
    try {
      if (editing) {
        await updateFaq.mutateAsync({
          faqId: editing.faqId,
          data: form,
        });
        toast.success('FAQ를 수정했습니다.');
      } else {
        await createFaq.mutateAsync(form);
        toast.success('FAQ를 추가했습니다.');
      }
      setOpen(false);
      refetch();
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleDelete = async (faq: FaqItem) => {
    const ok = await confirm({
      title: 'FAQ 삭제',
      description: `"${faq.question}" FAQ를 삭제하시겠습니까?`,
      confirmLabel: '삭제',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await deleteFaq.mutateAsync(faq.faqId);
      toast.success('삭제했습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FileText}
        title="FAQ"
        description="자주 묻는 질문 관리"
        actions={
          <MenuWriteGate>
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              FAQ 추가
            </Button>
          </MenuWriteGate>
        }
      />

      <ToolbarRow gap={8}>
        {FAQ_CATEGORIES.map((cat) => {
          const active = categoryFilter === cat.value;
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => setCategoryFilter(cat.value)}
              className={cn(
                'inline-flex items-center rounded-oval border px-3 py-1.5 text-sm font-medium transition-colors',
                active
                  ? 'border-ruda-primary bg-ruda-primary text-white'
                  : 'border-ruda-border-light bg-white text-ruda-text-secondary hover:bg-ruda-grey-50',
              )}
            >
              {cat.label}
            </button>
          );
        })}
      </ToolbarRow>

      {isLoading ? (
        <LoadingSpinner tip="FAQ를 불러오는 중..." />
      ) : (
        <DataCard>
          <div className="space-y-3 p-4">
          {filtered.map((faq) => (
            <Card key={faq.faqId}>
              <CardContent className="flex items-start justify-between gap-4 pt-4">
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={getFaqCategoryBadgeVariant(faq.category)}>
                      {getFaqCategoryLabel(faq.category)}
                    </Badge>
                  </div>
                  <p className="font-medium text-ruda-text-primary">
                    {faq.question}
                  </p>
                  <p className="whitespace-pre-wrap text-sm tracking-tight text-ruda-text-secondary">
                    {faq.answer}
                  </p>
                </div>
                <MenuWriteGate>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(faq)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(faq)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </MenuWriteGate>
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && (
            <p className="py-8 text-center text-muted-foreground">
              FAQ가 없습니다.
            </p>
          )}
          </div>
        </DataCard>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'FAQ 수정' : 'FAQ 추가'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>카테고리</Label>
              <Select
                value={form.category}
                onValueChange={(v) =>
                  setForm((prev) => ({ ...prev, category: v as FaqCategory }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FAQ_CATEGORIES.filter((c) => c.value !== 'ALL').map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>질문</Label>
              <Input
                value={form.question}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, question: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label>답변</Label>
              <textarea
                className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.answer}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, answer: e.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              취소
            </Button>
            <Button onClick={handleSave}>저장</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
